import React, { useMemo } from 'react'
import { motion } from 'motion/react'

import { EffectBackgroundProps } from '../types'

export const AnimatedGradientEffect = React.memo(({ layerStyle, index, values }: EffectBackgroundProps) => {
  const effectColors = useMemo(() => values.effectColors || ['#6366f1', '#8b5cf6', '#ec4899'], [values.effectColors])
  const effectSpeed = values.effectSpeed ?? 100
  const effectOpacity = values.effectOpacity ?? 100
  const opacityValue = effectOpacity / 100

  const gradientColors = effectColors.length >= 2 ? effectColors : [...effectColors, effectColors[0]]
  const color0 = gradientColors[0] || '#6366f1'
  const color1 = gradientColors[1] || '#8b5cf6'
  const color2 = gradientColors[2]
  const color3 = gradientColors[3]
  const safeSpeed = Math.max(effectSpeed, 1)

  return (
    <div
      style={{
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -index - 1,
        overflow: 'hidden',
      }}
    >
      {/* First gradient layer - rotating linear gradient */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(45deg, ${color0}, ${color1})`,
          opacity: opacityValue,
        }}
        animate={{
          background: [
            `linear-gradient(45deg, ${color0}, ${color1})`,
            `linear-gradient(135deg, ${color1}, ${color0})`,
            `linear-gradient(225deg, ${color0}, ${color1})`,
            `linear-gradient(315deg, ${color1}, ${color0})`,
            `linear-gradient(45deg, ${color0}, ${color1})`,
          ],
        }}
        transition={{
          duration: 8 * (100 / safeSpeed),
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Second gradient layer for more depth */}
      {color2 && (
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at center, ${color2}, transparent 70%)`,
            opacity: opacityValue * 0.6,
          }}
          animate={{
            scale: [1, 1.5, 1],
            x: ['0%', '20%', '0%'],
            y: ['0%', '20%', '0%'],
          }}
          transition={{
            duration: 10 * (100 / safeSpeed),
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}

      {/* Third gradient layer */}
      {color3 && (
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at center, ${color3}, transparent 70%)`,
            opacity: opacityValue * 0.5,
          }}
          animate={{
            scale: [1.5, 1, 1.5],
            x: ['0%', '-20%', '0%'],
            y: ['0%', '-20%', '0%'],
          }}
          transition={{
            duration: 12 * (100 / safeSpeed),
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
    </div>
  )
})
AnimatedGradientEffect.displayName = 'AnimatedGradientEffect'
